// Utility functions for Belgian structured reference format

export function isStructuredReference(reference: string): boolean {
  // Clean the reference first
  const cleaned = reference.replace(/[^0-9]/g, '');
  
  // Must be exactly 12 digits
  if (cleaned.length !== 12) return false;
  
  // Get the base number (first 10 digits) and check digits (last 2 digits)
  const base = parseInt(cleaned.slice(0, 10), 10);
  const checkDigits = parseInt(cleaned.slice(10, 12), 10);
  
  // Calculate expected check digits
  const remainder = base % 97;
  const expectedCheck = remainder === 0 ? 97 : remainder;
  
  // Check if valid
  return checkDigits === expectedCheck || checkDigits === 0;
}

export function formatBelgianReference(input: string): string {
  // Remove all non-digits
  const digits = input.replace(/\D/g, '').slice(0, 12);
  
  if (digits.length === 0) return '';
  
  // Format with +++ and slashes
  if (digits.length === 12) {
    return `+++${digits.slice(0, 3)}/${digits.slice(3, 7)}/${digits.slice(7, 12)}+++`;
  }
  
  // Partial formatting for incomplete numbers
  if (digits.length <= 3) {
    return `+++${digits}`;
  } else if (digits.length <= 7) {
    return `+++${digits.slice(0, 3)}/${digits.slice(3)}`;
  } else {
    return `+++${digits.slice(0, 3)}/${digits.slice(3, 7)}/${digits.slice(7)}`;
  }
}

export function validateBelgianReference(reference: string): boolean {
  // Remove all non-digits
  const digits = reference.replace(/\D/g, '');
  
  // Must be exactly 12 digits
  if (digits.length !== 12) return false;
  
  // Get the base number (first 10 digits) and check digits (last 2 digits)
  const base = parseInt(digits.slice(0, 10), 10);
  const checkDigits = parseInt(digits.slice(10, 12), 10);
  
  // Special case: if check digits are 00, it's valid
  if (checkDigits === 0) return true;
  
  // Calculate modulo 97
  const remainder = base % 97;
  
  // The check digits should equal the remainder (or 97 if remainder is 0)
  const expectedCheck = remainder === 0 ? 97 : remainder;
  
  return checkDigits === expectedCheck;
}

// Helper function to generate valid check digits
function generateCheckDigits(base: number): string {
  const remainder = base % 97;
  const checkDigits = remainder === 0 ? 97 : remainder;
  return checkDigits.toString().padStart(2, '0');
}